import { ArrowDownIcon, ArrowUpIcon, ChevronsUpDownIcon } from "lucide-react"
import type * as React from "react"
import { useTranslation } from "react-i18next"
import { TableHead } from "@/components/ui/table"
import { cn } from "@/lib/utils"

/**
 * A register column you can sort by (§11.2 amendment, 2026-08-15).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THREE STATES, CYCLED IN ONE PLACE. Unsorted → descending → ascending →
 * unsorted. Descending FIRST because every question the owner asks of a
 * register is "the biggest" or "the newest" — largest bill, latest date, most
 * recent number. Making him press twice to get the answer he wanted is the
 * commonest thing sortable tables get wrong.
 *
 * RETURNING TO UNSORTED IS A REAL STATE, not a dead third press: it restores
 * the register's own order (newest first), which is the view he works in.
 *
 * `aria-sort` ON THE HEADER, not just an arrow. A screen reader announces the
 * column as ascending or descending; an icon alone says nothing to it. The
 * arrows are also DIRECTIONAL rather than a generic caret, and the unsorted
 * state shows a faint double-chevron so a sortable column is discoverable
 * before it is pressed.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function SortableHead<TKey extends string>({
  column,
  label,
  sort,
  dir,
  onSort,
  className,
  align = "start",
}: {
  column: TKey
  label: string
  /** The column currently sorted, or undefined for the register's own order. */
  sort: TKey | undefined
  dir: "asc" | "desc" | undefined
  onSort: (sort: TKey | undefined, dir: "asc" | "desc" | undefined) => void
  className?: string
  align?: "start" | "end"
}): React.JSX.Element {
  const { t } = useTranslation()
  const active = sort === column
  const direction = active ? (dir ?? "desc") : undefined

  const next = () => {
    if (!active) return onSort(column, "desc")
    if (direction === "desc") return onSort(column, "asc")
    // Third press clears it — back to the register's own order.
    return onSort(undefined, undefined)
  }

  return (
    <TableHead
      className={cn("p-0", className)}
      aria-sort={
        direction === undefined ? "none" : direction === "asc" ? "ascending" : "descending"
      }
    >
      <button
        type="button"
        onClick={next}
        title={t("sort.hint")}
        className={cn(
          "hover:text-foreground flex w-full items-center gap-1 px-2 py-2 text-left transition-colors",
          align === "end" && "justify-end",
          active && "text-foreground font-medium",
        )}
      >
        {label}
        {direction === undefined ? (
          <ChevronsUpDownIcon aria-hidden="true" className="size-3.5 opacity-40" />
        ) : direction === "asc" ? (
          <ArrowUpIcon aria-hidden="true" className="size-3.5" />
        ) : (
          <ArrowDownIcon aria-hidden="true" className="size-3.5" />
        )}
      </button>
    </TableHead>
  )
}
